import { Button } from "../ui/button";
import { Form } from "../ui/form";
import { membersRegistrationFormSchema } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Loader2 } from "lucide-react";
import CustomInput from "../myProfile/CustomInput";
import { useToast } from "@/components/hooks/use-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { addNewMember } from "@/api/apiFunction";
import moment from "moment";
import { genderOptions } from "../myProfile/MyProfileForm";

interface UserInfo {
  data: {
    name?: string;
    email?: string;
    mobile?: string;
    user_detail?: {
      birthDate?: string;
      gender?: string;
      jerseySize?: string;
      bloodGroup?: string;
    };
    id?: string;
  };
}

interface MembersRegistrationFormProps {
  userInfo: UserInfo;
  dialougeClose: () => void;
  action: "add" | "edit";
}

const MembersRegistrationForm: React.FC<MembersRegistrationFormProps> = ({
  userInfo,
  dialougeClose,
  action = "add",
}): React.ReactElement => {
  const { toast } = useToast();
  const formSchema = membersRegistrationFormSchema();
  const queryClient = useQueryClient();

  const isoDate = userInfo?.data.user_detail?.birthDate;
  const formattedDate = isoDate ? moment(isoDate).format("YYYY-MM-DD") : "";
  console.log("userdate of birth", userInfo);
  // 1. Define Form
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: userInfo?.data?.name || "",
      dateOfBirth: formattedDate || undefined,
      gender: ["MALE", "FEMALE", "OTHER"].includes(
        userInfo?.data.user_detail?.gender || ""
      )
        ? (userInfo?.data.user_detail?.gender as "MALE" | "FEMALE" | "OTHER")
        : undefined,
      email: userInfo?.data?.email || "",
      mobile: userInfo?.data?.mobile || "",
      jerseySize: ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"].includes(
        userInfo?.data.user_detail?.jerseySize || ""
      )
        ? (userInfo?.data.user_detail?.jerseySize as
            | "XS"
            | "S"
            | "M"
            | "L"
            | "XL"
            | "2XL"
            | "3XL"
            | "4XL")
        : undefined,
      bloodGroup: ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].includes(
        userInfo?.data.user_detail?.bloodGroup || ""
      )
        ? (userInfo?.data.user_detail?.bloodGroup as
            | "A+"
            | "A-"
            | "B+"
            | "B-"
            | "AB+"
            | "AB-"
            | "O+"
            | "O-")
        : undefined,
      // sameAsPrimary: false,
    },
  });

  // add members
  const { mutate, isPending } = useMutation({
    //@ts-ignore
    mutationFn: (data) => addNewMember(data, action),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-members"] });

      toast({
        title: "New member is added successfully! ✅",
        variant: "default",
      });

      dialougeClose();
    },
    onError: (error: any) => {
      console.error("Error submitting form:", error);
      toast({
        title: "Something went wrong!",
        description: error?.response?.data?.message,
        variant: "destructive",
      });
    },
  });

  // console.log("userInfo", userInfo?.data?.id);
  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    try {
      const userData = {
        name: data.name,
        birthDate: data.dateOfBirth,
        gender: data.gender,
        email: data.email,
        mobile: data.mobile,
        phoneCode: "+91",
        jerseySize: data.jerseySize,
        bloodGroup: data.bloodGroup,
        memberUserId: userInfo?.data?.id,
        // sameAsPrimary: data.sameAsPrimary,
      };

      //@ts-ignore
      // setUser(userData);
      mutate(userData);
    } catch (error) {
      console.log(error);
    } finally {
      form.reset();
    }
  };

  return (
    <section className="w-full">
      <Form {...form}>
        {action === "edit" ? (
          <h1 className="text-center text-[#009FA6] font-semibold">
            Existing Member
          </h1>
        ) : (
          <h1 className="text-center text-[#009FA6] font-semibold">
            Add New Member
          </h1>
        )}
        <form className="space-y-4 " onSubmit={form.handleSubmit(onSubmit)}>
          <div className="w-full flex flex-col gap-6 mx-auto ">
            <div className="w-full  flex flex-col-reverse items-center  md:flex-row gap-6  justify-between">
              <div className=" flex flex-col gap-6 w-full">
                <CustomInput
                  control={form.control}
                  name="name"
                  label="Full Name"
                  placeholder="enter your name"
                  disabled={action === "edit" ? true : false}
                />

                <div
                  className={`flex w-full justify-between gap-6 ${
                    action === "edit" ? "hidden" : "block"
                  }`}
                >
                  <div
                    className={`relative w-full ${
                      action === "edit" ? "hidden" : "block"
                    }`}
                  >
                    <CustomInput
                      type="date"
                      control={form.control}
                      name="dateOfBirth"
                      label="Date Of Birth"
                      placeholder="dd/mm/yyy"
                      className="flex flex-col "
                      disabled={action === "edit" ? true : false}
                    />
                  </div>
                  <div
                    className={` w-full ${
                      action === "edit" ? "hidden" : "block"
                    }`}
                  >
                    <CustomInput
                      control={form.control}
                      name="bloodGroup"
                      label="Blood Group"
                      type="select"
                      className=""
                      placeholder="Select Blood  Group"
                      disabled={action === "edit" ? true : false}
                      options={[
                        "A+",
                        "A-",
                        "B+",
                        "B-",
                        "AB+",
                        "AB-",
                        "O+",
                        "O-",
                      ]}
                    />
                  </div>
                </div>

                <div className={` ${action === "edit" ? "hidden" : "block"}`}>
                  <CustomInput
                    control={form.control}
                    name="gender"
                    label="Gender"
                    type="radio"
                    className="flex"
                    options={genderOptions}
                    disabled={action === "edit" ? true : false}
                  />
                </div>
              </div>
            </div>
            {action === "add" && (
              <CustomInput
                control={form.control}
                name="email"
                label="Email"
                placeholder="enter your email"
              />
            )}

            <div>
              <CustomInput
                control={form.control}
                name="mobile"
                label="Mobile"
                placeholder="enter your mobile number"
                disabled={action === "edit" ? true : false}
              />
            </div>

            <div>
              <CustomInput
                control={form.control}
                name="jerseySize"
                label="T-Shirt / Jersey"
                type="radio"
                className="grid grid-cols-4 mt-2  md:flex md:flex-wrap  gap-6" // For horizontal alignment
                options={["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"]}
                //  disabled={action === "edit" ? true : false}
              />
            </div>

            <div className="flex flex-col justify-center  items-center gap-4">
              <Button
                type="submit"
                disabled={isPending}
                className="form-btn w-40 font-semibold px-5 md:px-10 rounded-3xl bg-gradient-to-r from-[#4BD5D6] to-[#009FA6] hover:opacity-90 transition-opacity"
              >
                {isPending ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 size={18} className="animate-spin" />
                    <span>{action === "edit" ? "Updating" : "Adding"}</span>
                  </div>
                ) : (
                  <span>Add Member</span>
                )}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </section>
  );
};

export default MembersRegistrationForm;
